// SPEC-131 (RN-002, ADR-001): única implementación de la fórmula de precio de venta.
// Prohibido duplicar estas fórmulas en componentes — todo cálculo de precio pasa por aquí.
//
// ADR-001: se mantiene la fórmula ya certificada de SPEC-095 (margen sobre precio de venta).
// SPEC-131 revierte el cambio de SPEC-119: el término de transporte vuelve a ser por línea
// (transporte_línea / cantidad_línea), no por bolsa completa (freight_bolsa /
// unidades_totales_bolsa). A diferencia de la fórmula pre-SPEC-119, transporte_línea ahora es
// un dato real persistido en `lineas_bolsa.transporte`, no una constante de sesión.
//
// SPEC-206 (ADR-001) — ESPEJO en el backend: el re-precio al reabrir/duplicar replica
// calcularPrecioUnitario, calcularPrecioVenta y costoConDescuento en
// cv-printing-backend/src/modules/cotizaciones/domain/ReprecioCotizacion.ts. Cualquier cambio aquí
// va también allá; la tabla reprecio-casos.json la recorren los dos lados (CA-034).

import { ColorVariante, OpcionPrecio, OrigenDescuento, TramoDescuento } from '../types';

export const TRANSPORTE_DEFAULT = 60000;

// RN-002 (SPEC-131, restaura RN-001 pre-SPEC-119): precio_unitario = round(precioBase + transporteLinea/cantidadLinea).
// precioBase = precio proveedor con descuento (SPEC-085) + precio marcación unitaria (SPEC-094).
export function calcularPrecioUnitario(
  precioBase: number,
  transporteLinea: number,
  cantidadLinea: number,
): number {
  const perUnidad = cantidadLinea > 0 ? transporteLinea / cantidadLinea : 0;
  return Math.round(precioBase + perUnidad);
}

// RN-002 (SPEC-095, sin cambios — ver ADR-001): precio_venta = round( precioUnitario / ((100-util)/100) ).
export function calcularPrecioVenta(precioUnitario: number, utilidad: number): number {
  const factor = (100 - Math.min(utilidad, 99.99)) / 100;
  if (factor <= 0) return precioUnitario;
  return Math.round(precioUnitario / factor);
}

// RN-003 (SPEC-095, sin cambios — ver ADR-001): utilidad = round( 100 × (1 − precioUnitario / precioVenta) , 2 ).
export function calcularUtilidad(precioUnitario: number, precioVenta: number): number {
  if (precioUnitario <= 0 || precioVenta <= precioUnitario) return 0;
  const util = 100 * (1 - precioUnitario / precioVenta);
  return Math.round(util * 100) / 100;
}

// RN-005/SPEC-094: "pp" combinado es el precio proveedor (con descuento) más el precio de
// marcación unitario ya confirmado (0 si no hay marcación). Es el precioBase de calcularPrecioUnitario.
export function precioProveedorConMarcacion(state: { precioProveedorActual: number; precioMarcacion: number }): number {
  return state.precioProveedorActual + state.precioMarcacion;
}


// SPEC-195 VAL-001 — tramo que contiene `monto` (límites inclusivos, `montoHasta` null = sin tope);
// null si ninguno lo contiene (hueco). Misma regla y misma tabla de casos que el backend (CA-020).
export function seleccionarTramoDescuento(tramos: TramoDescuento[], monto: number): TramoDescuento | null {
  return tramos.find((t) => t.montoDesde <= monto && (t.montoHasta === null || t.montoHasta >= monto)) ?? null;
}

// SPEC-195 VAL-002 (SPEC-085/174) — costo con descuento del proveedor: precio de lista descontado
// y redondeado si el % es mayor que 0; si no, el precio de lista.
export function costoConDescuento(precioLista: number, descuentoProveedor: number): number {
  return descuentoProveedor > 0 ? Math.round(precioLista * (1 - descuentoProveedor / 100)) : precioLista;
}

// SPEC-207 (RN-006, RN-008, RN-009, RN-012) — ESPEJO del resolutor único de
// cv-printing-backend/src/modules/cotizaciones/domain/DescuentoPorTipoPrecio.ts (resolverVariante,
// resolverLinea): el backend lo usa al agregar, guardar, cambiar la cantidad y re-preciar; aquí se
// verifica la paridad con la tabla descuento-tipo-precio.casos.json (CA-018). Cualquier cambio aquí va
// también allá, y viceversa. Ninguna rama mira el proveedor (RNF-006).
export type TipoPrecio = 'normal' | 'outlet' | 'neto' | null;

export interface VarianteDescuento {
  color: string;
  precio: number;
  precio_lista: number | null;
  tipo_precio: TipoPrecio;
  descuento_adicional: number | null;
  precio_costo: number | null;
}

// G: el descuento general del proveedor para el monto de la línea, y si salió de un tramo.
export interface General {
  porcentaje: number;
  desdeTramo: boolean;
}

export interface DescuentoResuelto {
  porcentaje: number;
  origen: OrigenDescuento;
}

function redondear2(valor: number): number {
  return Math.round(valor * 100) / 100;
}

function normalizarColor(color: string): string {
  return color.trim().toLowerCase();
}

// RN-006 — el primer paso que aplica: costo informado → neto → Outlet en cascada (solo sin tramos,
// RN-012) → G.
export function resolverVariante(variante: VarianteDescuento, g: General): DescuentoResuelto {
  if (variante.precio_costo != null && variante.precio_lista != null) {
    return { porcentaje: redondear2(100 * (1 - variante.precio_costo / variante.precio_lista)), origen: 'costo_proveedor' };
  }
  if (variante.tipo_precio === 'neto') return { porcentaje: 0, origen: 'neto' };
  if (variante.tipo_precio === 'outlet' && !g.desdeTramo) {
    const adicional = variante.descuento_adicional ?? 0;
    return { porcentaje: redondear2(100 * (1 - (1 - g.porcentaje / 100) * (1 - adicional / 100))), origen: 'outlet' };
  }
  return { porcentaje: g.porcentaje, origen: g.desdeTramo ? 'tramo' : 'general' };
}

// Con el mismo %, gana el origen del paso anterior del resolutor (no depende del orden); un color
// sin origen (snapshot sin él) va al final.
const PRECEDENCIA: Record<OrigenDescuento, number> = { costo_proveedor: 0, neto: 1, outlet: 2, tramo: 3, general: 3 };

function menor<T extends { porcentaje: number; origen: OrigenDescuento | null }>(a: T, b: T): T {
  if (a.porcentaje !== b.porcentaje) return a.porcentaje < b.porcentaje ? a : b;
  const rango = (o: OrigenDescuento | null) => (o === null ? 4 : PRECEDENCIA[o]);
  return rango(b.origen) < rango(a.origen) ? b : a;
}

// RN-009 — el menor % entre las variantes del color elegido, con su origen; RN-008 — sin color, o si
// el color ya no está, el menor entre todas. `null` sin variantes: el llamador aplica G.
export function resolverLinea(
  variantes: VarianteDescuento[],
  colorSeleccionado: string | null,
  g: General,
): DescuentoResuelto | null {
  if (variantes.length === 0) return null;

  const color = colorSeleccionado != null ? normalizarColor(colorSeleccionado) : null;
  const delColor = color != null ? variantes.filter((v) => normalizarColor(v.color) === color) : [];
  const candidatas = delColor.length > 0 ? delColor : variantes;
  return candidatas.map((v) => resolverVariante(v, g)).reduce(menor);
}

// RN-014 — el tooltip muestra "Lista · Descuento" con un % mayor que 0, o con origen costo_proveedor
// o neto aunque el % sea 0. Sin preguntar por el proveedor.
export function mostrarListaYDescuento(porcentaje: number, origen: OrigenDescuento | null | undefined): boolean {
  return porcentaje > 0 || origen === 'costo_proveedor' || origen === 'neto';
}

// RN-011 — % y origen del color elegido según el snapshot `colores_variantes` (el backend ya
// resolvió cada variante); un color repetido toma el menor (RN-009). `null` si el color no trae %
// (línea anterior a la spec): el detalle conserva el % y el origen de la línea. `origen` es null si
// el color trae % pero no origen.
export function descuentoDelColor(
  colores: ColorVariante[] | undefined,
  colorSeleccionado: string,
): { porcentaje: number; origen: OrigenDescuento | null } | null {
  const color = normalizarColor(colorSeleccionado);
  const delColor = (colores ?? []).filter((c) => normalizarColor(c.color) === color && c.descuento_proveedor !== undefined);
  if (delColor.length === 0) return null;
  return delColor
    .map((c) => ({ porcentaje: c.descuento_proveedor as number, origen: c.origen_descuento ?? null }))
    .reduce(menor);
}

// SPEC-208 (RN-010) — ESPEJO de etiquetaOpcion en
// cv-printing-backend/src/modules/cotizaciones/domain/OpcionPrecio.ts; la tabla
// opciones-precio.casos.json la recorren los dos lados (CA-033). Solo de presentación: espacios y
// saltos de línea colapsados, extremos recortados y sin el "/" inicial; sin texto, "Opción i + 1".
export function etiquetaOpcion(opcion: OpcionPrecio, indice: number): string {
  const texto = (opcion.descripcion ?? '').replace(/\s+/g, ' ').trim().replace(/^\//, '').trim();
  return texto === '' ? `Opción ${indice + 1}` : texto;
}
